/**
 * The split rule editor (TMMAPP-0112; TMMAPI-0148, MM-D-123; flagged: undrawn, built from the
 * prototype's dialog and form patterns). Name; "Match by" the served name, the contributor and / or a
 * minimum amount (at least one); the target rows — an envelope and / or a category, Fixed or %, and
 * the amount — and the note "The rest stays in Unassigned." Saves the whole rule (create, or a `PUT`
 * replace). The server resolves FIXED, then PERCENT, capped at what the part brings in.
 */
import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Category } from '@/api/categories';
import type { Envelope } from '@/api/envelopes';
import type { SplitRule, SplitRuleInput, SplitTargetMode } from '@/api/splitRules';
import { denseFieldSx } from '@/components/denseField';
import { GuardedDialog } from '@/components/GuardedDialog';
import { AddIcon, CloseIcon } from '@/components/icons';
import { SubmitButton } from '@/components/SubmitButton';
import { minorToInputText, parseMoneyToMinor } from '@/i18n/format';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';
import { resolveTokens } from '@/theme/theme';

export interface SplitRuleMember {
  userId: number;
  name: string | null;
}

interface TargetDraft {
  key: number;
  envelopeId: string;
  categoryId: string;
  mode: SplitTargetMode;
  amount: string;
}

interface SplitRuleEditorDialogProps {
  rule?: SplitRule;
  currency: string;
  envelopes: readonly Envelope[];
  categories: readonly Category[];
  members: readonly SplitRuleMember[];
  busy: boolean;
  /** The last write's error, shown inside the dialog. */
  error: string | null;
  errorTick: number;
  onClose: () => void;
  onSubmit: (input: SplitRuleInput) => void;
}

const MAX_TARGETS = 20;
// Row keys only (React's list identity), never sent.
let lastTargetKey = 0;
const newTargetKey = () => ++lastTargetKey;
const TITLE_ID = 'split-rule-editor-title';

function percentText(bp: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2, useGrouping: false }).format(
    bp / 100,
  );
}

/** A percent typed in the person's locale, as basis points (1–10000); `null` when it isn't one. */
function parsePercent(text: string, locale: string): number | null {
  const decimal = new Intl.NumberFormat(locale)
    .formatToParts(1.5)
    .find((p) => p.type === 'decimal');
  const normalized = text
    .trim()
    .replace('%', '')
    .replace(decimal?.value ?? '.', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const bp = Math.round(Number(normalized) * 100);
  return bp >= 1 && bp <= 10000 ? bp : null;
}

export function SplitRuleEditorDialog({
  rule,
  currency,
  envelopes,
  categories,
  members,
  busy,
  error,
  errorTick,
  onClose,
  onSubmit,
}: SplitRuleEditorDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const dense = denseFieldSx(tokens);
  const nameRef = useRef<HTMLInputElement>(null);
  const draftOf = (target: SplitRule['targets'][number] | null): TargetDraft => ({
    key: newTargetKey(),
    envelopeId: target?.envelopeId != null ? String(target.envelopeId) : '',
    categoryId: target?.categoryId != null ? String(target.categoryId) : '',
    mode: target?.mode ?? 'FIXED',
    amount:
      target === null
        ? ''
        : target.mode === 'FIXED'
          ? target.amountMinor !== null
            ? minorToInputText(target.amountMinor, currency, locale)
            : ''
          : target.bp !== null
            ? percentText(target.bp, locale)
            : '',
  });
  const [name, setName] = useState(rule?.name ?? '');
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [matchName, setMatchName] = useState(rule?.matchName ?? '');
  const [contributor, setContributor] = useState(
    rule?.matchContributorUserId != null ? String(rule.matchContributorUserId) : '',
  );
  const [minAmount, setMinAmount] = useState(
    rule?.minAmountMinor != null ? minorToInputText(rule.minAmountMinor, currency, locale) : '',
  );
  const [targets, setTargets] = useState<TargetDraft[]>(() =>
    rule !== undefined && rule.targets.length > 0 ? rule.targets.map(draftOf) : [draftOf(null)],
  );
  const [tried, setTried] = useState(false);
  const { dirty } = useDirty({ name, enabled, matchName, contributor, minAmount, targets });
  const guard = useDialogDirtyGuard(dirty, onClose, busy, errorTick);

  const minMinor = minAmount.trim() === '' ? null : parseMoneyToMinor(minAmount, currency, locale);
  const resolved = targets.map((row) => ({
    row,
    value:
      row.mode === 'FIXED'
        ? parseMoneyToMinor(row.amount, currency, locale)
        : parsePercent(row.amount, locale),
  }));
  const problems = {
    name: name.trim().length === 0,
    match: matchName.trim() === '' && contributor === '' && minAmount.trim() === '',
    minAmount: minAmount.trim() !== '' && (minMinor === null || minMinor <= 0),
    target: resolved.some(
      ({ row, value }) =>
        (row.envelopeId === '' && row.categoryId === '') || value === null || value <= 0,
    ),
    percent:
      resolved
        .filter(({ row }) => row.mode === 'PERCENT')
        .reduce((sum, { value }) => sum + (value ?? 0), 0) > 10000,
  };
  const invalid = Object.values(problems).some(Boolean);

  function patchTarget(key: number, patch: Partial<TargetDraft>) {
    setTargets((rows) => rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function submit() {
    setTried(true);
    if (invalid) return;
    onSubmit({
      name: name.trim(),
      enabled,
      ...(matchName.trim() !== '' ? { matchName: matchName.trim() } : {}),
      ...(contributor !== '' ? { matchContributorUserId: Number(contributor) } : {}),
      ...(minMinor !== null ? { minAmountMinor: minMinor } : {}),
      ...(rule !== undefined ? { priority: rule.priority } : {}),
      targets: resolved.map(({ row, value }) => ({
        mode: row.mode,
        ...(row.envelopeId !== '' ? { envelopeId: Number(row.envelopeId) } : {}),
        ...(row.categoryId !== '' ? { categoryId: Number(row.categoryId) } : {}),
        ...(row.mode === 'FIXED' ? { amountMinor: value ?? 0 } : { bp: value ?? 0 }),
      })),
    });
  }

  const liveCategories = categories.filter((category) => category.archivedAt === null);

  return (
    <GuardedDialog
      guard={guard}
      titleId={TITLE_ID}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: { sx: { width: 600, maxWidth: 'calc(100% - 32px)' } },
        transition: { onEntered: () => nameRef.current?.focus() },
      }}
    >
      <DialogTitle id={TITLE_ID}>
        {t(rule === undefined ? 'splitRules.newTitle' : 'splitRules.editTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack sx={{ gap: 2, mt: 0.5 }}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('splitRules.name')}
            value={name}
            required
            inputRef={nameRef}
            onChange={(event) => setName(event.target.value)}
            error={tried && problems.name}
            helperText={tried && problems.name ? t('splitRules.nameRequired') : null}
            slotProps={{ htmlInput: { maxLength: 120 } }}
            sx={dense}
          />
          <Box role="group" aria-labelledby="split-rule-match">
            <Typography id="split-rule-match" variant="subtitle2" component="h3">
              {t('splitRules.matchBy')}
            </Typography>
            <Typography sx={{ fontSize: 12, color: tokens.text2, mb: 1 }}>
              {t('splitRules.matchHelp')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1.25 }}>
              <TextField
                label={t('splitRules.matchName')}
                value={matchName}
                onChange={(event) => setMatchName(event.target.value)}
                slotProps={{ htmlInput: { maxLength: 120 } }}
                sx={{ flex: 2, ...dense }}
              />
              <FormControl sx={{ flex: 2, ...dense }}>
                <InputLabel id="split-rule-contributor">{t('splitRules.contributor')}</InputLabel>
                <Select
                  labelId="split-rule-contributor"
                  label={t('splitRules.contributor')}
                  value={contributor}
                  onChange={(event) => setContributor(String(event.target.value))}
                >
                  <MenuItem value="">{t('splitRules.anyContributor')}</MenuItem>
                  {members.map((member) => (
                    <MenuItem key={member.userId} value={String(member.userId)}>
                      {personName(member.name, t)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={t('splitRules.minAmount')}
                value={minAmount}
                onChange={(event) => setMinAmount(event.target.value)}
                error={problems.minAmount}
                helperText={problems.minAmount ? t('splitRules.minAmountInvalid') : null}
                sx={{ flex: 1, ...dense }}
              />
            </Stack>
            {tried && problems.match && (
              <Typography role="alert" sx={{ fontSize: 12, color: tokens.errorText, mt: 0.5 }}>
                {t('splitRules.matchRequired')}
              </Typography>
            )}
          </Box>
          <Box role="group" aria-labelledby="split-rule-targets">
            <Typography id="split-rule-targets" variant="subtitle2" component="h3" sx={{ mb: 1 }}>
              {t('splitRules.targets')}
            </Typography>
            <Stack sx={{ gap: 1.25 }}>
              {targets.map((row, index) => (
                <Stack
                  key={row.key}
                  data-split-target=""
                  direction={{ xs: 'column', sm: 'row' }}
                  sx={{ gap: 1, alignItems: { sm: 'center' } }}
                >
                  <FormControl sx={{ flex: 2, minWidth: 0, ...dense }}>
                    <InputLabel id={`split-target-envelope-${row.key}`}>
                      {t('splitRules.envelope')}
                    </InputLabel>
                    <Select
                      labelId={`split-target-envelope-${row.key}`}
                      label={t('splitRules.envelope')}
                      value={row.envelopeId}
                      onChange={(event) =>
                        patchTarget(row.key, { envelopeId: String(event.target.value) })
                      }
                    >
                      <MenuItem value="">{t('splitRules.noEnvelope')}</MenuItem>
                      {envelopes.map((envelope) => (
                        <MenuItem key={envelope.id} value={String(envelope.id)}>
                          {envelope.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ flex: 2, minWidth: 0, ...dense }}>
                    <InputLabel id={`split-target-category-${row.key}`}>
                      {t('splitRules.category')}
                    </InputLabel>
                    <Select
                      labelId={`split-target-category-${row.key}`}
                      label={t('splitRules.category')}
                      value={row.categoryId}
                      onChange={(event) =>
                        patchTarget(row.key, { categoryId: String(event.target.value) })
                      }
                    >
                      <MenuItem value="">{t('splitRules.noCategory')}</MenuItem>
                      {liveCategories.map((category) => (
                        <MenuItem key={category.id} value={String(category.id)}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ flex: 1, minWidth: 96, ...dense }}>
                    <InputLabel id={`split-target-mode-${row.key}`}>
                      {t('splitRules.mode')}
                    </InputLabel>
                    <Select
                      labelId={`split-target-mode-${row.key}`}
                      label={t('splitRules.mode')}
                      value={row.mode}
                      onChange={(event) =>
                        patchTarget(row.key, {
                          mode: event.target.value as SplitTargetMode,
                          amount: '',
                        })
                      }
                    >
                      <MenuItem value="FIXED">{t('splitRules.fixed')}</MenuItem>
                      <MenuItem value="PERCENT">{t('splitRules.percent')}</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label={t(row.mode === 'FIXED' ? 'splitRules.amount' : 'splitRules.share')}
                    value={row.amount}
                    onChange={(event) => patchTarget(row.key, { amount: event.target.value })}
                    error={
                      tried &&
                      (resolved[index]?.value === null || (resolved[index]?.value ?? 0) <= 0)
                    }
                    sx={{ flex: 1, minWidth: 96, ...dense }}
                  />
                  <IconButton
                    aria-label={t('splitRules.removeTarget', { n: index + 1 })}
                    disabled={targets.length === 1}
                    onClick={() => setTargets((rows) => rows.filter((r) => r.key !== row.key))}
                  >
                    <CloseIcon />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            {tried && problems.target && (
              <Typography role="alert" sx={{ fontSize: 12, color: tokens.errorText, mt: 0.5 }}>
                {t('splitRules.targetRequired')}
              </Typography>
            )}
            {problems.percent && (
              <Typography role="alert" sx={{ fontSize: 12, color: tokens.errorText, mt: 0.5 }}>
                {t('splitRules.percentTooHigh')}
              </Typography>
            )}
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={targets.length >= MAX_TARGETS}
              onClick={() => setTargets((rows) => [...rows, draftOf(null)])}
              sx={{ mt: 1 }}
            >
              {t('splitRules.addTarget')}
            </Button>
            <Typography sx={{ fontSize: 12, color: tokens.text2, mt: 1 }} data-rest-note="">
              {t('splitRules.restNote')}
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
            }
            label={t('splitRules.enabled')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={guard.requestClose} disabled={busy}>
          {t('common.cancel')}
        </Button>
        <SubmitButton loading={busy} onClick={submit}>
          {t('common.save')}
        </SubmitButton>
      </DialogActions>
    </GuardedDialog>
  );
}
